/** Shared helpers for coach document issue/expiry dates. */

export const EXPIRY_HORIZON_DAYS = 92; // ~3 months

export interface ExpiryInfo {
  daysLeft: number;
  overdue: boolean;
  /** Human readable time left, e.g. "2 months left" or "3 days overdue" */
  label: string;
  tone: "expired" | "critical" | "warning" | "ok";
}

const MS_PER_DAY = 86400000;

export function daysUntil(dateStr: string, from: Date = new Date()): number {
  const target = new Date(dateStr);
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  return Math.round((b - a) / MS_PER_DAY);
}

function humanise(days: number): string {
  const d = Math.abs(days);
  if (d === 0) return "today";
  if (d < 14) return `${d} day${d === 1 ? "" : "s"}`;
  if (d < 60) {
    const w = Math.round(d / 7);
    return `${w} week${w === 1 ? "" : "s"}`;
  }
  const m = Math.round(d / 30);
  return `${m} month${m === 1 ? "" : "s"}`;
}

export function getExpiryInfo(
  expiryDate: string | null | undefined,
  from: Date = new Date()
): ExpiryInfo | null {
  if (!expiryDate) return null;
  const daysLeft = daysUntil(expiryDate, from);
  const overdue = daysLeft < 0;
  const label = overdue
    ? daysLeft === 0
      ? "expires today"
      : `${humanise(daysLeft)} overdue`
    : daysLeft === 0
      ? "expires today"
      : `${humanise(daysLeft)} left`;

  const tone: ExpiryInfo["tone"] = overdue
    ? "expired"
    : daysLeft <= 14
      ? "critical"
      : daysLeft <= EXPIRY_HORIZON_DAYS
        ? "warning"
        : "ok";

  return { daysLeft, overdue, label, tone };
}

export function expiryBadgeClass(tone: ExpiryInfo["tone"]): string {
  switch (tone) {
    case "expired":
      return "bg-destructive/15 text-destructive border-destructive/30";
    case "critical":
      return "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30";
    case "warning":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}
